import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { useT } from '../../../i18n';
import type { ApiError } from '../../../lib/errors';
import { khoaApiRepository } from '../../../lib/repositories/khoa-api.repository';
import { useToast } from '../../../lib/toast';
import { KHOA_VAI_TRO, thuLai } from '../../vai-tro/hooks/useVaiTro';
import { MOT_TRANG } from '../constants';
import type { ApiKey, ApiKeyCreate, ApiKeyCreated } from '../types';

export const KHOA_KHOA_API = {
  danhSach: ['api-keys', 'danh-sach'] as const,
};

/** Trần số trang khi nối: chặn vòng lặp nếu server trả `next_cursor` lặp lại. */
const TRANG_TOI_DA = 20;

/**
 * Mọi khoá của tổ chức, nối `next_cursor` tới hết rồi lọc ở client: tối đa 50
 * khoá còn hiệu lực cộng khoá đã thu hồi, API chưa có tham số lọc.
 */
export function useDanhSachKhoaApi() {
  return useQuery<ApiKey[], ApiError>({
    queryKey: KHOA_KHOA_API.danhSach,
    queryFn: async () => {
      const ra: ApiKey[] = [];
      let cursor: string | undefined;
      for (let i = 0; i < TRANG_TOI_DA; i += 1) {
        const trang = await khoaApiRepository.danhSach({ limit: MOT_TRANG, cursor });
        ra.push(...trang.items);
        cursor = trang.next_cursor ?? undefined;
        if (!cursor) break;
      }
      return ra;
    },
    staleTime: 30_000,
    retry: thuLai,
  });
}

/**
 * Tạo khoá. Không gửi `Idempotency-Key` (ADR-0068 mục 3) và không thử lại:
 * lần bấm lại sau lỗi mạng có thể tạo khoá thứ hai, thu hồi được ở danh sách.
 * Không bật toast — hộp hiện bí mật là phản hồi của việc này.
 */
export function useTaoKhoaApi() {
  const queryClient = useQueryClient();
  return useMutation<ApiKeyCreated, ApiError, ApiKeyCreate>({
    mutationFn: (du) => khoaApiRepository.tao(du),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: KHOA_KHOA_API.danhSach }),
    onError: (err) => {
      // Quyền vừa không gán được vì module bị gỡ: tải lại danh mục để lưới nói đúng hiện trạng.
      if (err.status === 422) void queryClient.invalidateQueries({ queryKey: KHOA_VAI_TRO.danhMucQuyen });
    },
  });
}

/** Thu hồi. `404` là khoá vừa không còn ở phiên khác: tải lại danh sách. */
export function useThuHoiKhoaApi() {
  const queryClient = useQueryClient();
  const toast = useToast();
  const { t } = useT();
  return useMutation<void, ApiError, ApiKey>({
    mutationFn: (k) => khoaApiRepository.thuHoi(k.id),
    onSuccess: (_kq, k) => {
      queryClient.setQueryData<ApiKey[]>(KHOA_KHOA_API.danhSach, (cu) =>
        cu?.map((x) => (x.id === k.id ? { ...x, status: 'REVOKED', revoked_at: new Date().toISOString() } : x)),
      );
      void queryClient.invalidateQueries({ queryKey: KHOA_KHOA_API.danhSach });
      toast.success(t('khoaApi.daThuHoi', { ten: k.name }));
    },
    onError: (err) => {
      if (err.status === 404) void queryClient.invalidateQueries({ queryKey: KHOA_KHOA_API.danhSach });
    },
  });
}
